import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuote } from '../contexts/QuoteContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowRight, User, Mail, MapPin, AlertTriangle, Loader2, Search, Users, Building2, Briefcase, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StepIndicator = ({ currentStep }) => (
    <div className="flex items-center justify-center gap-2 mb-6">
        {[1, 2, 3].map((step) => (
            <div
                key={step}
                className={`h-2 transition-all duration-300 ${
                    step === currentStep 
                        ? 'w-8 bg-brand-lime' 
                        : step < currentStep 
                            ? 'w-2 bg-brand-lime' 
                            : 'w-2 bg-brand-gray-200'
                }`}
            />
        ))}
    </div>
);

const CustomerDetails = () => {
    const navigate = useNavigate();
    const { user, token } = useAuth();
    const { customerDetails, setCustomerDetails } = useQuote();
    const [loading, setLoading] = useState(false);
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(true);
    const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const [customerSearchQuery, setCustomerSearchQuery] = useState('');
    const [selectedCompany, setSelectedCompany] = useState(null);

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        try {
            const response = await axios.get(`${API}/customers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCustomers(response.data);
        } catch (error) {
        } finally {
            setLoadingCustomers(false);
        }
    };

    const handleEmailBlur = async () => {
        if (!customerDetails.email) return;

        try {
            const response = await axios.post(
                `${API}/quotes/check-duplicate`,
                null,
                {
                    params: { customer_email: customerDetails.email },
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.has_duplicate) {
                setDuplicateWarning(response.data.existing_quote);
            } else {
                setDuplicateWarning(null);
            }
        } catch (error) {
        }
    };

    const handleSelectCompany = (customer) => {
        setSelectedCompany(customer);
        setCustomerDialogOpen(false);
        
        // If company has only one contact, auto-select it
        if (customer.contacts?.length === 1) {
            const contact = customer.contacts[0];
            setCustomerDetails({
                company_name: customer.name,
                contact_name: contact.name,
                contact_job_title: contact.job_title || '',
                email: contact.email,
                phone: contact.phone || customer.phone || '',
                address: customer.address
            });
            setDuplicateWarning(null);
            toast.success(`Selected ${contact.name} at ${customer.name}`);
        } else if (customer.contacts?.length > 1) {
            // Show contact selection dialog
            setContactDialogOpen(true);
        } else {
            // No contacts, just fill company info
            setCustomerDetails({
                company_name: customer.name,
                contact_name: '',
                contact_job_title: '',
                email: '',
                phone: customer.phone || '',
                address: customer.address
            });
            toast.success(`Selected ${customer.name}`);
        }
    };

    const handleSelectContact = (contact) => {
        setCustomerDetails({
            company_name: selectedCompany.name,
            contact_name: contact.name,
            contact_job_title: contact.job_title || '',
            email: contact.email,
            phone: contact.phone || selectedCompany.phone || '',
            address: selectedCompany.address
        });
        setContactDialogOpen(false);
        setDuplicateWarning(null);
        toast.success(`Selected ${contact.name} at ${selectedCompany.name}`);
    };

    const canSubmit = customerDetails.company_name && customerDetails.contact_name && customerDetails.email && customerDetails.address && !duplicateWarning && !loading;

    const handleContinueClick = (e) => {
        if (!canSubmit) {
            e.preventDefault();
            if (duplicateWarning) {
                toast.error('An active quote already exists for this customer');
            } else {
                toast.error('Please fill in all fields');
            }
        }
    };

    const handleInputChange = (field, value) => {
        setCustomerDetails(prev => ({ ...prev, [field]: value }));
        if (field === 'email') {
            setDuplicateWarning(null);
        }
    };

    const filteredCustomers = customers.filter(customer => {
        const searchLower = customerSearchQuery.toLowerCase();
        const matchesName = customer.name.toLowerCase().includes(searchLower);
        const matchesContacts = customer.contacts?.some(c => 
            c.name.toLowerCase().includes(searchLower) ||
            c.email.toLowerCase().includes(searchLower)
        );
        return matchesName || matchesContacts;
    });

    return (
        <div className="max-w-lg mx-auto animate-fade-in">
            <StepIndicator currentStep={1} />

            <Card className="shadow-lg border-0">
                <CardHeader>
                    <CardTitle className="text-2xl font-bold font-heading">
                        Customer Details
                    </CardTitle>
                    <CardDescription>
                        Select a customer or enter details manually
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-5">
                        {/* Engineer Info (Read-only) */}
                        <div className="p-4 bg-brand-gray-50 border border-brand-gray-200">
                            <Label className="text-xs font-bold uppercase tracking-wide text-brand-gray-500">
                                Engineer
                            </Label>
                            <div className="flex items-center gap-3 mt-2">
                                <div className="h-10 w-10 bg-brand-lime flex items-center justify-center">
                                    <User className="h-5 w-5 text-black" />
                                </div>
                                <div>
                                    <p className="font-medium text-brand-charcoal">{user?.name}</p>
                                    <p className="text-sm text-brand-gray-500">{user?.email}</p>
                                </div>
                            </div>
                        </div>

                        {/* Select Existing Customer Button */}
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCustomerDialogOpen(true)}
                            className="w-full h-12 border-2 border-dashed border-brand-gray-300 hover:border-brand-lime hover:bg-brand-lime/5"
                            data-testid="select-existing-customer-btn"
                        >
                            <Building2 className="h-5 w-5 mr-2 text-brand-gray-500" />
                            <span className="text-brand-gray-600">Select Existing Customer</span>
                        </Button>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-brand-gray-200"></div>
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-white px-2 text-brand-gray-400">
                                    or enter manually
                                </span>
                            </div>
                        </div>

                        {/* Company Name */}
                        <div className="space-y-2">
                            <Label htmlFor="companyName" className="text-xs font-bold uppercase tracking-wide">
                                Company Name
                            </Label>
                            <div className="relative">
                                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                                <Input
                                    id="companyName"
                                    type="text"
                                    placeholder="Enter company name"
                                    value={customerDetails.company_name || ''}
                                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                                    className="h-12 pl-10"
                                    data-testid="customer-company-input"
                                />
                            </div>
                        </div>

                        {/* Contact Name */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label htmlFor="contactName" className="text-xs font-bold uppercase tracking-wide">
                                    Contact Name
                                </Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                                    <Input
                                        id="contactName"
                                        type="text"
                                        placeholder="Contact name"
                                        value={customerDetails.contact_name || ''}
                                        onChange={(e) => handleInputChange('contact_name', e.target.value)}
                                        className="h-12 pl-10"
                                        data-testid="customer-contact-name-input"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="jobTitle" className="text-xs font-bold uppercase tracking-wide">
                                    Job Title
                                </Label>
                                <div className="relative">
                                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                                    <Input
                                        id="jobTitle"
                                        type="text"
                                        placeholder="Job title"
                                        value={customerDetails.contact_job_title || ''}
                                        onChange={(e) => handleInputChange('contact_job_title', e.target.value)}
                                        className="h-12 pl-10"
                                        data-testid="customer-job-title-input"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Customer Email */}
                        <div className="space-y-2">
                            <Label htmlFor="customerEmail" className="text-xs font-bold uppercase tracking-wide">
                                Contact Email
                            </Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                                <Input
                                    id="customerEmail"
                                    type="email"
                                    placeholder="contact@company.com"
                                    value={customerDetails.email || ''}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    onBlur={handleEmailBlur}
                                    className={`h-12 pl-10 ${duplicateWarning ? 'border-red-500 focus:ring-red-500' : ''}`}
                                    data-testid="customer-email-input"
                                />
                            </div>
                        </div>

                        {/* Phone Number */}
                        <div className="space-y-2">
                            <Label htmlFor="customerPhone" className="text-xs font-bold uppercase tracking-wide">
                                Phone Number
                            </Label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                                <Input
                                    id="customerPhone"
                                    type="tel"
                                    placeholder="Phone number"
                                    value={customerDetails.phone || ''}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    className="h-12 pl-10"
                                    data-testid="customer-phone-input"
                                />
                            </div>
                        </div>

                        {/* Duplicate Warning */}
                        {duplicateWarning && (
                            <Alert variant="destructive" className="bg-red-50 border-red-200">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>
                                    <strong>Duplicate Quote Found!</strong>
                                    <br />
                                    Quote {duplicateWarning.reference_number} was created by {duplicateWarning.engineer_name} on{' '}
                                    {new Date(duplicateWarning.created_at).toLocaleDateString()}.
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Customer Address */}
                        <div className="space-y-2">
                            <Label htmlFor="customerAddress" className="text-xs font-bold uppercase tracking-wide">
                                Company Address
                            </Label>
                            <div className="relative">
                                <MapPin className="absolute left-3 top-3 h-5 w-5 text-brand-gray-400" />
                                <textarea
                                    id="customerAddress"
                                    placeholder="Enter full address"
                                    value={customerDetails.address || ''}
                                    onChange={(e) => handleInputChange('address', e.target.value)}
                                    className="w-full min-h-[100px] pl-10 pr-4 py-3 bg-background border border-input text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-none"
                                    data-testid="customer-address-input"
                                />
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-4">
                            <Link
                                to={canSubmit ? "/select-pack" : "#"}
                                onClick={handleContinueClick}
                                className={`w-full h-12 bg-[#7AC943] hover:bg-[#68B035] text-black font-bold uppercase tracking-wide inline-flex items-center justify-center gap-2 rounded-md transition-colors ${!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                data-testid="customer-details-continue-btn"
                            >
                                {loading ? (
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                    <>
                                        Continue to Pack Selection
                                        <ArrowRight className="h-5 w-5 ml-2" />
                                    </>
                                )}
                            </Link>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Customer Selection Dialog */}
            <Dialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen}>
                <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            Select Company
                        </DialogTitle>
                        <DialogDescription>
                            Choose a company, then select a contact
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                            <Input
                                type="text"
                                placeholder="Search companies or contacts..."
                                value={customerSearchQuery}
                                onChange={(e) => setCustomerSearchQuery(e.target.value)}
                                className="h-11 pl-10"
                                data-testid="customer-search-input"
                            />
                        </div>

                        {/* Customer List */}
                        <div className="max-h-[400px] overflow-y-auto space-y-2">
                            {loadingCustomers ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-brand-lime" />
                                </div>
                            ) : filteredCustomers.length === 0 ? (
                                <div className="text-center py-8">
                                    <Users className="h-10 w-10 mx-auto text-brand-gray-300 mb-2" />
                                    <p className="text-brand-gray-500">No customers found</p>
                                </div>
                            ) : (
                                filteredCustomers.map(customer => (
                                    <button
                                        key={customer.id}
                                        type="button"
                                        onClick={() => handleSelectCompany(customer)}
                                        className="w-full p-4 text-left border border-brand-gray-200 hover:border-brand-lime hover:bg-brand-lime/5 transition-colors"
                                        data-testid={`select-customer-${customer.id}`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <h4 className="font-semibold text-brand-charcoal">
                                                    {customer.name}
                                                </h4>
                                                <div className="flex items-center gap-2 mt-1 text-sm text-brand-gray-500">
                                                    <Users className="h-3.5 w-3.5" />
                                                    {customer.contacts?.length || 0} contact{customer.contacts?.length !== 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        {customer.contacts?.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {customer.contacts.slice(0, 3).map((contact) => (
                                                    <span key={contact.id || contact.email} className="text-xs px-2 py-1 bg-brand-gray-100 text-brand-gray-600">
                                                        {contact.name}
                                                    </span>
                                                ))}
                                                {customer.contacts.length > 3 && (
                                                    <span className="text-xs px-2 py-1 bg-brand-gray-100 text-brand-gray-500">
                                                        +{customer.contacts.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Contact Selection Dialog */}
            <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            Select Contact
                        </DialogTitle>
                        <DialogDescription>
                            Choose a contact at {selectedCompany?.name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-2">
                        {selectedCompany?.contacts?.map((contact, idx) => (
                            <button
                                key={contact.id || idx}
                                type="button"
                                onClick={() => handleSelectContact(contact)}
                                className="w-full p-4 text-left border border-brand-gray-200 hover:border-brand-lime hover:bg-brand-lime/5 transition-colors"
                                data-testid={`select-contact-${contact.id || idx}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 bg-brand-gray-100 flex items-center justify-center">
                                        <User className="h-5 w-5 text-brand-gray-500" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-brand-charcoal">{contact.name}</p>
                                        {contact.job_title && (
                                            <p className="text-sm text-brand-gray-500">{contact.job_title}</p>
                                        )}
                                        <p className="text-sm text-brand-gray-400">{contact.email}</p>
                                        {(contact.phone || selectedCompany?.phone) && (
                                            <p className="text-sm text-brand-gray-400 flex items-center gap-1">
                                                <Phone className="h-3 w-3" /> {contact.phone || selectedCompany.phone}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CustomerDetails;
